import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import type { DiscoveryPicksPatch, DiscoverySearch } from '@discovr/types';
import { discoveryApi } from '../api/discoveryApi';
import { discoveryKeys, invalidateSettledSearch } from '../queryKeys';

export function useStartSearch(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DiscoverySearch) => {
      const outcome = await discoveryApi.startSearch(campaignId, body);
      if ('jobId' in outcome) {
        const { jobId } = outcome;
        await queryClient.prefetchQuery({
          queryKey: discoveryKeys.search(campaignId, jobId),
          queryFn: () => discoveryApi.search(campaignId, jobId),
        });
      }
      return outcome;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.campaign(campaignId) });
    },
  });
}

export function useCancelSearch(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, keepPartial }: { jobId: string; keepPartial: boolean }) =>
      discoveryApi.cancel(campaignId, jobId, keepPartial),
    onSuccess: (_, { jobId }) => invalidateSettledSearch(queryClient, campaignId, jobId),
  });
}

/**
 * Each answer carries every pick, so concurrent patches can land out of order; once the last one settles the
 * picks are read again, and that read is what stays.
 */
export function usePatchPicks(campaignId: string) {
  const queryClient = useQueryClient();
  const mutationKey = discoveryKeys.picksPatch(campaignId);
  return useMutation({
    mutationKey,
    mutationFn: (patch: DiscoveryPicksPatch) => discoveryApi.patchPicks(campaignId, patch),
    onSuccess: (picks) => queryClient.setQueryData(discoveryKeys.picks(campaignId), picks),
    onSettled: async () => {
      if (queryClient.isMutating({ mutationKey }) > 1) return;
      await queryClient.invalidateQueries({ queryKey: discoveryKeys.picks(campaignId) });
    },
  });
}

const isPicksPatch = (value: unknown): value is DiscoveryPicksPatch =>
  typeof value === 'object' &&
  value !== null &&
  'platformIds' in value &&
  Array.isArray(value.platformIds);

export function usePendingPicks(campaignId: string): DiscoveryPicksPatch[] {
  return useMutationState({
    filters: { mutationKey: discoveryKeys.picksPatch(campaignId), status: 'pending' },
    select: (mutation) => mutation.state.variables,
  }).filter(isPicksPatch);
}
